import { useEffect, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Avatar, Box, Stack, Typography, useTheme } from '@mui/material';
import { Crosshair, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import type { GetLeadsTheSameAddressQuery } from '@graphql/__generated__/graphql';
import { CountBadge } from '@modules/_shared/components/CountBadge';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import {
  translateAppraisalReason,
  translateBuyHorizon,
  translateSaleHorizon,
} from '@modules/_shared/utils/lead-labels';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

type OtherLeadsListItemProps = {
  lead: GetLeadsTheSameAddressQuery['leads_the_same_address'][number];
};

const OtherLeadsListItemLink = ({ lead }: OtherLeadsListItemProps) => {
  const { t } = useLocale();
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;

  const firstName = lead.contact?.first_name ?? null;
  const lastName = lead.contact?.last_name ?? null;
  const fullName =
    firstName != null && lastName != null
      ? `${firstName} ${lastName}`
      : t('No name');

  const link = `/leads/${lead.id}`;
  const translationParts: string[] = [];

  if (lead.appraisal_reason) {
    translationParts.push(translateAppraisalReason(t, lead.appraisal_reason));
  }

  if (lead.sale_horizon) {
    translationParts.push(translateSaleHorizon(t, lead.sale_horizon));
  } else if (lead.buy_horizon) {
    translationParts.push(translateBuyHorizon(t, lead.buy_horizon));
  }

  const qualification = translationParts.join(' • ');
  const agentCount = lead.lead_agents_aggregate?.aggregate?.count ?? 0;
  const infoLineParts: string[] = [];

  if (qualification) {
    infoLineParts.push(`${t('Qualification')}: ${qualification}`);
  }

  infoLineParts.push(`${t('agents')}: ${agentCount}`);

  return (
    <Box
      component={Link}
      to={link}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: 'flex',
        cursor: 'pointer',
        alignItems: 'center',
        gap: 1.25,
        px: 1.5,
        py: 1,
        mx: -1.5,
        borderRadius: '8px',
        transition: 'background-color 0.15s ease',
        '&:hover': {
          bgcolor: 'grey.50',
          '& .external-icon': { opacity: 1 },
        },
      }}
    >
      <Avatar
        sx={{
          width: 36,
          height: 36,
          bgcolor: `color-mix(in srgb, ${palette.primary.main} 8%, transparent)`,
          color: palette.primary.main,
        }}
      >
        <Crosshair size={16} />
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          noWrap
          sx={{ fontWeight: 500, fontSize: '0.875rem', lineHeight: 1.3 }}
        >
          {fullName}
        </Typography>
        <Typography
          variant="caption"
          noWrap
          sx={{
            color: 'text.secondary',
            lineHeight: 1.3,
          }}
        >
          {infoLineParts.join(' • ')}
        </Typography>
      </Box>
      <ExternalLink
        size={14}
        className="external-icon"
        style={{
          opacity: 0,
          color: customPalette.gray400,
          flexShrink: 0,
          transition: 'opacity 0.15s ease',
        }}
      />
    </Box>
  );
};

const GET_LEADS_THE_SAME_ADDRESS = gql(/* GraphQL */ `
  query GetLeadsTheSameAddress($leadId: uuid!) {
    leads_the_same_address(args: { p_lead_id: $leadId }) {
      id
      completed
      contact {
        id
        first_name
        last_name
      }
      appraisal_reason
      sale_horizon
      buy_horizon
      lead_agents_aggregate {
        aggregate {
          count
        }
      }
    }
  }
`);

type LeadDetailsOtherLeadsCardProps = {
  leadId: string;
};

export const LeadDetailsOtherLeadsCard = ({
  leadId,
}: LeadDetailsOtherLeadsCardProps) => {
  const { t } = useLocale();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const { data, error } = useQuery(GET_LEADS_THE_SAME_ADDRESS, {
    variables: { leadId },
  });

  useEffect(() => {
    if (error) {
      setErrorLike(error);
    }
  }, [error]);

  const leads = [];
  for (const lead of data?.leads_the_same_address ?? []) {
    if (lead != null && lead.completed && lead.id !== leadId) {
      leads.push(lead);
    }
  }

  return (
    <ListingAccordion
      title={
        <>
          {t('Other leads at this address')}
          {leads.length > 0 && <CountBadge count={leads.length} />}
        </>
      }
    >
      {leads.length === 0 ? (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('No other leads at this address yet')}
        </Typography>
      ) : (
        <Stack
          sx={{
            gap: 0.5,
          }}
        >
          {leads.map(lead => (
            <OtherLeadsListItemLink lead={lead} key={lead.id} />
          ))}
        </Stack>
      )}
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </ListingAccordion>
  );
};
